"use client";

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { TRPCClient } from "./provider";

// --- Types ---

export interface ActiveBatch {
  batchId: string;
  batchName: string;
  platform: "META" | "TIKTOK" | "GOOGLE" | "SNAPCHAT";
  orgSlug?: string;
}

export interface BatchAdStatus {
  id: string;
  fileName: string;
  fileType: string;
  status: string;
  metaAdId?: string | null;
  tiktokAdId?: string | null;
  googleAdId?: string | null;
  errorUserMessage?: string | null;
}

export interface BatchAdsData {
  batchId: string;
  batchName: string;
  status: string;
  platform: string;
  totalAds: number;
  successfulAds: number;
  failedAds: number;
  processingAds: number;
  ads: BatchAdStatus[];
}

export interface BatchStatusContextValue {
  activeBatches: ActiveBatch[];
  batchData: Record<string, BatchAdsData>;
  trackBatch: (batch: ActiveBatch) => void;
  dismissBatch: (batchId: string) => void;
  isExpanded: boolean;
  toggleExpanded: () => void;
  hasBatches: boolean;
}

// --- Context ---

const BatchStatusContext = createContext<BatchStatusContextValue | null>(null);

export function useBatchStatus(): BatchStatusContextValue {
  const ctx = useContext(BatchStatusContext);
  if (!ctx) {
    throw new Error(
      "useBatchStatus must be used within a <BatchStatusProvider>",
    );
  }
  return ctx;
}

// --- Poller component for a single batch ---

function BatchPoller({
  batch,
  trpc,
  onData,
  onTerminal,
}: {
  batch: ActiveBatch;
  trpc: TRPCClient;
  onData: (batchId: string, data: BatchAdsData) => void;
  onTerminal: (batchId: string) => void;
}) {
  const terminalTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const query = trpc.creativeTestingRouter.getBatchAdsStatus.useQuery(
    { batchId: batch.batchId, slug: batch.orgSlug },
    {
      enabled: true,
      refetchInterval: (q: {
        state: { data: { status?: string } | undefined };
      }) => {
        const data = q.state.data;
        if (
          data?.status === "ACTIVE" ||
          data?.status === "FAILED" ||
          data?.status === "PARTIALLY_FAILED"
        ) {
          return false;
        }
        return 1000;
      },
      refetchOnWindowFocus: false,
    },
  );

  useEffect(() => {
    if (query.data) {
      onData(batch.batchId, query.data as BatchAdsData);

      const isTerminal =
        query.data.status === "ACTIVE" ||
        query.data.status === "FAILED" ||
        query.data.status === "PARTIALLY_FAILED";

      if (isTerminal && !terminalTimerRef.current) {
        terminalTimerRef.current = setTimeout(() => {
          onTerminal(batch.batchId);
        }, 30000);
      }
    }
  }, [query.data, batch.batchId, onData, onTerminal]);

  useEffect(() => {
    return () => {
      if (terminalTimerRef.current) {
        clearTimeout(terminalTimerRef.current);
      }
    };
  }, []);

  return null;
}

// --- Provider ---

const SESSION_STORAGE_KEY = "newform_active_batches";

function loadPersistedBatches(): ActiveBatch[] {
  if (typeof window === "undefined") return [];
  try {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function persistBatches(batches: ActiveBatch[]) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(batches));
  } catch {
    // Ignore storage errors
  }
}

export function BatchStatusProvider({
  children,
  trpc,
}: {
  children: ReactNode;
  trpc: TRPCClient;
}) {
  const [activeBatches, setActiveBatches] = useState<ActiveBatch[]>([]);
  const [batchData, setBatchData] = useState<Record<string, BatchAdsData>>({});
  const [isExpanded, setIsExpanded] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // Load from sessionStorage only on client after mount
  useEffect(() => {
    setActiveBatches(loadPersistedBatches());
    setHydrated(true);
  }, []);

  const trackBatch = useCallback((batch: ActiveBatch) => {
    setActiveBatches((prev) => {
      if (prev.some((b) => b.batchId === batch.batchId)) return prev;
      const next = [...prev, batch];
      persistBatches(next);
      return next;
    });
    setIsExpanded(true);
  }, []);

  const dismissBatch = useCallback((batchId: string) => {
    setActiveBatches((prev) => {
      const next = prev.filter((b) => b.batchId !== batchId);
      persistBatches(next);
      return next;
    });
    setBatchData((prev) => {
      const next = { ...prev };
      delete next[batchId];
      return next;
    });
  }, []);

  const handleData = useCallback((batchId: string, data: BatchAdsData) => {
    setBatchData((prev) => ({ ...prev, [batchId]: data }));
  }, []);

  const handleTerminal = useCallback(
    (batchId: string) => {
      // Auto-dismiss after 30s — batch already reached terminal state
      dismissBatch(batchId);
    },
    [dismissBatch],
  );

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  return (
    <BatchStatusContext.Provider
      value={{
        activeBatches,
        batchData,
        trackBatch,
        dismissBatch,
        isExpanded,
        toggleExpanded,
        hasBatches: activeBatches.length > 0,
      }}
    >
      {children}
      {hydrated &&
        activeBatches.map((batch) => (
          <BatchPoller
            key={batch.batchId}
            batch={batch}
            trpc={trpc}
            onData={handleData}
            onTerminal={handleTerminal}
          />
        ))}
    </BatchStatusContext.Provider>
  );
}
